/**
 * Reiter „Verlauf“: was mit der Ausschreibung geschah, in Sätzen („Du hast die Ausschreibung
 * übernommen.“), nach Tagen gruppiert und filterbar. Personen statt Kennungen, keine Engine-Wörter
 * (`TASK_COMPLETED`, „Prozess RUNNING“), keine Hash-IDs — die Formulierung steht in
 * `tenderPanelText.describeHistoryEntry`.
 *
 * Gefiltert wird nach dem, was der Verlauf heute kennt: Entscheidungen und Termine. Uploads und
 * Ortskorrekturen schreibt der Server (noch) nicht in den Verlauf, also gibt es dafür keinen Filter,
 * der immer leer bliebe.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import type { TenderActivityEntry } from "../../TenderSearchProfileAPI";
import { describeHistoryEntry, groupByDay, type HistoryCategory } from "./tenderPanelText";

type Filter = "all" | Exclude<HistoryCategory, "other">;

const FILTERS: Filter[] = ["all", "decision", "dates"];

export function TenderPanelHistory({
  entries,
  loading,
  currentUser,
  people,
}: {
  entries: TenderActivityEntry[] | undefined;
  loading: boolean;
  currentUser: string | null;
  people: Record<string, string>;
}) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<Filter>("all");
  const lines = useMemo(
    () => (entries ?? []).map((entry) => describeHistoryEntry(entry, currentUser, people)),
    [entries, currentUser, people]
  );
  const shown = filter === "all" ? lines : lines.filter((line) => line.category === filter);
  const groups = groupByDay(shown, new Date());

  if (loading && !entries) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 size={14} className="animate-spin" aria-hidden /> {t("tenderBoard.loading")}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="tender-panel-history">
      <div className="flex flex-wrap gap-2" role="group" aria-label={t("tenderBoard.panel.history.filter")}>
        {FILTERS.map((key) => (
          <button
            key={key}
            type="button"
            aria-pressed={filter === key}
            onClick={() => setFilter(key)}
            className={`rounded-full border px-3 py-1 text-sm ${
              filter === key ? "border-foreground bg-foreground text-background" : "border-border hover:bg-muted"
            }`}
          >
            {t(`tenderBoard.panel.history.${key}`)}
          </button>
        ))}
      </div>

      {groups.length === 0 ? (
        <div className="text-sm text-muted-foreground">{t("tenderBoard.trail.empty")}</div>
      ) : (
        groups.map((group) => (
          <section key={group.label}>
            <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{group.label}</h3>
            <ol className="space-y-3">
              {group.lines.map((line, index) => (
                <li key={`${line.at}-${index}`} className="flex gap-3">
                  <span className="w-11 shrink-0 pt-0.5 text-xs tabular-nums text-muted-foreground">
                    {new Date(line.at).toLocaleTimeString("de-DE", {
                      timeZone: "Europe/Berlin",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                  <span
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                      line.isSystem ? "bg-foreground text-background" : "border border-border bg-muted"
                    }`}
                    title={line.who ?? t("tenderBoard.system")}
                  >
                    {line.initials}
                  </span>
                  <div className="min-w-0 flex-1 text-sm">
                    <div className="font-medium">{line.text}</div>
                    {line.detail && <div className="text-xs text-muted-foreground">{line.detail}</div>}
                  </div>
                </li>
              ))}
            </ol>
          </section>
        ))
      )}
    </div>
  );
}
